import IntervalTree from 'node-interval-tree';
import start_data from '../asset/start.json';
import { useState, useEffect } from 'react';

// State is managed by the module itself.
// These are initialized only once when the module is first imported.
const per_vol_tree: { [key: string]: IntervalTree<string> } = {};
let is_initiated = false;

function book2pdf(book_page: number, vol_name: string): number {
  const startOffset = (start_data as any)[vol_name] || 0;
  let ret_page = book_page + startOffset;

  switch (vol_name) {
    case 'EN/Volume_10':
      if (book_page > 398) return ret_page + 4;
      break;
    case 'EN/Volume_15':
      if (book_page > 704) return ret_page + 2;
      break;
    case 'EN/Volume_17_01':
      if (book_page > 24) return ret_page + 4;
      if (book_page > 4) return ret_page + 2;
      break;
    case 'EN/Volume_17_02':
      if (book_page > 300) return ret_page + 2;
      break;
    case 'MR/Khand_18_1':
      if (book_page > 565) return ret_page + 3;
      if (book_page > 306) return ret_page + 2;
      break;
    case 'MR/Khand_18_2':
      if (book_page > 432) return ret_page + 2;
      break;
    case 'MR/Khand_18_3':
      if (book_page > 198) return ret_page + 2;
      break;
    case 'MR/Khand_19':
      if (book_page > 544) return ret_page + 4;
      if (book_page > 400) return ret_page + 2;
      break;
    default:
      return ret_page;
  }
  return ret_page;
}

let init_promise: Promise<void> | null = null;

export function initiate_interval_tree(): Promise<void> {
  if (is_initiated) {
    return Promise.resolve(); // Already done
  }
  if (init_promise) {
    return init_promise;
  }

  init_promise = (async () => {
    try {
      const [en, mr, hi] = await Promise.all([
        import('../asset/suggestion.json'),
        import('../asset/suggestion-mr.json'),
        import('../asset/suggestion-hi.json'),
      ]);

      const suggestions = [
        ...(en.default || en),
        ...(mr.default || mr),
        ...(hi.default || hi),
      ];

      for (const suggestion of suggestions) {
        if (!per_vol_tree[suggestion.vol_name]) {
          per_vol_tree[suggestion.vol_name] = new IntervalTree<string>();
        }

        const tree = per_vol_tree[suggestion.vol_name];
        const page_start = book2pdf(suggestion.page_start, suggestion.vol_name);
        const page_end = book2pdf(suggestion.page_end, suggestion.vol_name);

        const to_insert = { ...suggestion, page_start, page_end };

        try {
          tree.insert(page_start, page_end, JSON.stringify(to_insert));
        } catch (ex) {
          console.log('error inserting into interval tree');
        }
      }
      is_initiated = true;
    } catch (e) {
      console.error('Failed to initialize interval tree', e);
    }
  })();

  return init_promise;
}

export function useIntervalTreeReady() {
  const [isReady, setIsReady] = useState(is_initiated);
  useEffect(() => {
    if (!is_initiated) {
      initiate_interval_tree().then(() => setIsReady(true));
    } else {
      setIsReady(true);
    }
  }, []);
  return isReady;
}

export function getUrlContext(url: string) {
  const splitURL = url.split('/');
  if (splitURL[2] !== 'baws') return null;
  if (splitURL[3] !== 'EN' && splitURL[3] !== 'MR' && splitURL[3] !== 'HI')
    return null;

  const container = splitURL[3] + '/' + splitURL[4];
  const page = parseInt(splitURL[6]);

  let to_return = per_vol_tree[container]
    ? per_vol_tree[container].search(page, page)
    : null;

  if (to_return && to_return.length === 0) {
    const tuple = { label: container };
    to_return = [JSON.stringify(tuple)];
  }

  return to_return;
}

export function getPageContext(item: {
  actual_page: { raw: string };
  container: { raw: string };
  path: { raw: string };
}) {
  const to_find = parseInt(item.actual_page.raw);

  const pathParts = item.path.raw.split('/');
  const lang = pathParts.length > 2 ? pathParts[2] : '';
  const container = lang ? `${lang}/${item.container.raw}` : item.container.raw;

  if (per_vol_tree[container]) {
    return per_vol_tree[container].search(to_find, to_find);
  }
  return [];
}
